import React, { Fragment } from "react";
import { Badge, Icon, Loader, Modal, Pagination, Thumbnail } from "@culturehq/components";

import profilePlaceholder from "images/profile-placeholder.png";

import { ProfileLink } from "app/links";
import { Trans } from "app/utils";

import Control from "components/editor/controls/Control";
import { canViewSkillsField } from "app/permissions";
import { useProfile } from "app/context";
import getUserName from "app/utils/getUserName";
import ChqModal from "./common/ChqModal";

const NotifyMessage = ({ newUsers, usersPagination }) => {
  if (newUsers.length === 0 && usersPagination?.totalCount) {
    return (
      <>
        <Trans>Notifying</Trans>
        {" "}
        {usersPagination?.totalCount}
        {" "}
        <Trans>{usersPagination?.totalCount > 1 ? "people" : "person"}</Trans>
      </>
    );
  }

  if (newUsers.length > 0 && !usersPagination?.totalCount) {
    return (
      <>
        <Trans>Creating</Trans>
        {" "}
        {newUsers.length}
        {" "}
        <Trans>new user{newUsers.length > 1 ? "s" : ""}</Trans>
      </>
    );
  }

  if (newUsers.length > 0 && usersPagination?.totalCount) {
    return (
      <>
        <Trans>Notifying</Trans>
        {" "}
        {usersPagination?.totalCount}
        {" "}
        <Trans>{usersPagination?.totalCount > 1 ? "people" : "person"}</Trans>
        {newUsers.length > 0 && (
          <>
            {" "}
            <Trans>and creating</Trans>
            {" "}
            {newUsers.length}
            {" "}
            <Trans>new user{newUsers.length > 1 ? "s" : ""}</Trans>
          </>
        )}
      </>
    );
  }

  return <Trans>Not notifying any people yet</Trans>;
};

const MentionHeaderGroup = ({ group, prefix, icon }) => {
  if (group.length === 0) {
    return null;
  }

  return (
    <>
      {prefix}
      {" "}
      {group.map(({ entityId, userId, label }) => (
        <Fragment key={entityId || userId}>
          <Badge primary icon={icon}>{label}</Badge>
          {" "}
        </Fragment>
      ))}
    </>
  );
};

const MentionHeader = ({ options, totalCount }) => {
  const groups = {
    companies: [],
    departments: [],
    interests: [],
    locations: [],
    organizations: [],
    skills: [],
    users: []
  };

  options.forEach(option => groups[option.category || "users"]?.push(option));
  const { companies, departments, interests, locations, organizations, skills, users } = groups;
  const entityCount = locations.length + departments.length + interests.length + companies.length;
  const { profile } = useProfile();
  const { organization } = profile;

  return (
    <div className="chq-mention-list--hd">
      {totalCount} {totalCount === 1 ? "person " : "people "}
      {organizations.length === 0 && entityCount > 0 && (
        <>
          <MentionHeaderGroup group={locations} prefix="in" icon="loc-ol" />
          <MentionHeaderGroup group={companies} prefix="in" icon="diagram" />
          <MentionHeaderGroup group={departments} prefix="in" icon="diagram" />
          <MentionHeaderGroup group={interests} prefix="who like" icon="heart-ol" />
          {canViewSkillsField(organization) && (<MentionHeaderGroup group={skills} prefix="skilled in" icon="hammer" />)}
          <MentionHeaderGroup group={users} prefix="as well as" icon="person" />
        </>
      )}
    </div>
  );
};

const UsersModal = ({ usersPagination, loading, selectedOptions, users, fetchUsers }) => {
  const newUsers = selectedOptions.filter(option => !!option.email);
  return (
    <div className="sharing-modal">
      <ChqModal
        className="UsersModal"
        entrance="zoomIn"
        trigger={onTrigger => (
          <Control onClick={onTrigger} disabled={(!usersPagination || usersPagination.totalCount === 0) && newUsers.length === 0} className="chq-edi--ct-right">
            <div className="sharing-with-container">
              <Icon icon="user-filled" />
              <NotifyMessage newUsers={newUsers} usersPagination={usersPagination} />
            </div>
          </Control>
        )}
      >
        <Modal.Heading>
          <Trans>Who will be notified?</Trans>
        </Modal.Heading>
        <Modal.Body>
          <Loader loading={loading}>
            <MentionHeader
              options={selectedOptions}
              totalCount={(usersPagination?.totalCount || 0) + newUsers.length}
              newUsers={newUsers}
            />
            <div className="chq-mention-list">
              {users.map(user => (
                <ProfileLink key={user.id} user={user}>
                  <Thumbnail image={user.avatar.thumbUrl} />
                  {getUserName(user)}
                  <br />
                  <small>{user.title}</small>
                </ProfileLink>
              ))}
              {newUsers.map(user => (
                <div className="new-user" key={user.email}>
                  <Thumbnail image={profilePlaceholder} />
                  {user.label}
                  <br />
                  <small>{user.email}</small>
                </div>
              ))}
            </div>
            {usersPagination?.totalCount && (
              <Pagination {...usersPagination} onClick={fetchUsers} />)}
          </Loader>
        </Modal.Body>
      </ChqModal>
    </div>
  );
};

export default UsersModal;
